import React, { useState } from 'react';
import { X, MessageSquare, Send, Loader2, CheckCircle, AlertCircle } from 'lucide-react';
import { sendSMS } from '../api/sms';

/**
 * SMS Templates for quick messaging
 */
const SMS_TEMPLATES = [
  {
    id: 'follow_up',
    name: 'Follow Up',
    message: 'Hi {name}, this is {agent} from CloudMortgage. Following up on your mortgage inquiry. Let me know if you have any questions!'
  },
  {
    id: 'appointment',
    name: 'Appointment',
    message: 'Hi {name}, just a reminder about our call scheduled for today. Looking forward to speaking with you!'
  },
  {
    id: 'documents',
    name: 'Documents',
    message: 'Hi {name}, we need a few documents to proceed with your application. Please check your email for details. Thanks!'
  },
  {
    id: 'rate_update',
    name: 'Rate Update',
    message: 'Hi {name}, great news! Rates have dropped. Would you like to discuss your options? Give me a call when you can.'
  },
  {
    id: 'thank_you',
    name: 'Thank You',
    message: 'Hi {name}, thank you for choosing CloudMortgage! We\'re here to help with any questions.'
  }
];

/**
 * Format phone number for display
 */
function formatPhoneNumber(phone) {
  if (!phone) return '';
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
}

/**
 * Normalize phone to E.164 format
 */
function normalizePhone(phone) {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (phone.startsWith('+')) return phone;
  return `+${digits}`;
}

/**
 * Simple SMS Modal for sending text messages
 */
function SMSModal({ isOpen, onClose, contact, agentName = 'Your Agent' }) {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  if (!isOpen) return null;

  const handleTemplateSelect = (template) => {
    // Replace placeholders with actual values
    let text = template.message;
    text = text.replace('{name}', contact?.contactName?.split(' ')[0] || 'there');
    text = text.replace('{agent}', agentName);
    setMessage(text);
  };

  const handleSend = async () => {
    if (!message.trim()) return;
    if (!contact?.phoneNumber) {
      setError('No phone number available');
      return;
    }

    setSending(true);
    setError(null);

    try {
      await sendSMS({
        toPhone: normalizePhone(contact.phoneNumber),
        message: message.trim(),
        recipientName: contact.contactName,
        journeyId: contact.leadId
      });

      setSent(true);
      setTimeout(() => {
        onClose();
        // Reset state after close
        setTimeout(() => {
          setMessage('');
          setSent(false);
        }, 300);
      }, 1500);
    } catch (err) {
      console.error('SMS send error:', err);
      setError(err.message || 'Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const charCount = message.length;
  const isOverLimit = charCount > 160;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900 dark:text-white">
                Send Text Message
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                To: {contact?.contactName || 'Unknown'} • {formatPhoneNumber(contact?.phoneNumber)}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Success State */}
          {sent ? (
            <div className="py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h4 className="text-lg font-medium text-slate-900 dark:text-white mb-1">
                Message Sent!
              </h4>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Your text message has been delivered.
              </p>
            </div>
          ) : (
            <>
              {/* Quick Templates */}
              <div className="mb-4">
                <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">
                  QUICK TEMPLATES
                </p>
                <div className="flex flex-wrap gap-2">
                  {SMS_TEMPLATES.map((template) => (
                    <button
                      key={template.id}
                      onClick={() => handleTemplateSelect(template)}
                      className="px-3 py-1.5 text-xs font-medium bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 rounded-full hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                    >
                      {template.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* Message Input */}
              <div className="mb-4">
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Type your message..."
                  rows={4}
                  className={`w-full px-4 py-3 rounded-xl border ${
                    isOverLimit
                      ? 'border-red-300 dark:border-red-700 focus:ring-red-500'
                      : 'border-slate-200 dark:border-slate-700 focus:ring-blue-500'
                  } bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 resize-none`}
                />
                <div className="flex justify-between mt-1">
                  <span className={`text-xs ${isOverLimit ? 'text-red-500' : 'text-slate-400'}`}>
                    {charCount}/160 characters
                  </span>
                  {isOverLimit && (
                    <span className="text-xs text-red-500">
                      Message will be split into multiple SMS
                    </span>
                  )}
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                  <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
                </div>
              )}

              {/* Send Button */}
              <button
                onClick={handleSend}
                disabled={!message.trim() || sending}
                className="w-full py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-300 dark:disabled:bg-slate-700 text-white font-medium rounded-xl transition-colors flex items-center justify-center gap-2"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Message
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default SMSModal;
